import { writeFetch, getSession } from '../api';
import {
  secureUpload,
  deleteDocument as deleteDocumentFromServer,
  submitAuthorization as submitAuthorizationToServer,
  fetchAuthorizations as fetchAuthorizationsFromServer,
} from './s3UploadService';

const TABLE = 'docuvault_files_1748500000';

// ── SHA-256 checksum (browser-native) ─────────────────────────────────────────
export async function computeChecksum(file) {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return `checksum_${Date.now()}`;
  }
}

/**
 * uploadDocument
 * Phase-aware pipeline: checksum → presign → S3 upload → NCB record
 */
export async function uploadDocument(file, metadata, onProgress) {
  const user = getSession();
  if (!user) throw new Error('Not authenticated');

  const notify = (pct, phase) =>
    typeof onProgress === 'function' && onProgress(pct, phase);

  notify(0, 'checksum');
  const checksum = await computeChecksum(file);
  notify(100, 'checksum');

  notify(0, 'presign');
  const s3Result = await secureUpload({
    file,
    ownerId: user.id,
    category: metadata.category || 'general',
    onProgress: (pct) => {
      if (pct === 0) notify(0, 'upload');
      else notify(pct, 'upload');
    },
  });

  notify(0, 'metadata');
  const record = {
    id: crypto.randomUUID(),
    owner_id: user.id,
    file_name: `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`,
    original_name: file.name,
    file_type: file.type || 'application/octet-stream',
    file_size: file.size,
    file_category: metadata.category || 'general',
    s3_key: s3Result.s3Key,
    s3_url: s3Result.s3Url,
    s3_bucket: s3Result.bucket,
    encryption_status: 'AES256',
    access_level: metadata.accessLevel || 'private',
    authorized_parties: JSON.stringify(metadata.authorizedParties || []),
    authorization_required: metadata.authorizationRequired ? 1 : 0,
    authorization_threshold: metadata.authorizationThreshold || 1,
    authorization_approvals: JSON.stringify([]),
    document_status: 'active',
    description: metadata.description || '',
    tags: JSON.stringify(metadata.tags || []),
    expiry_date: metadata.expiryDate || null,
    version: 1,
    checksum,
  };

  const res = await writeFetch(`create/${TABLE}`, {
    method: 'POST',
    body: JSON.stringify(record),
  });

  notify(100, 'metadata');
  return res.json();
}

// ── Fetch documents ─────────────────────────────────────────────────────────
export async function fetchDocuments(filters = {}) {
  const user = getSession();
  if (!user) return [];

  let query = `owner_id=${user.id}&document_status=active&sort=created_at&order=desc&limit=100`;
  if (filters.category && filters.category !== 'all') {
    query += `&file_category=${filters.category}`;
  }

  const res  = await writeFetch(`read/${TABLE}?${query}`);
  const data = await res.json();
  return Array.isArray(data.data) ? data.data : [];
}

// ── Delete document (atomic S3 + NCB) ────────────────────────────────────────
export async function deleteDocument(id, s3Key) {
  const user = getSession();
  if (!user) throw new Error('Not authenticated');

  const result = await deleteDocumentFromServer({
    ownerId:  user.id,
    s3Key:    s3Key || undefined,
    recordId: id !== undefined && id !== null ? id : undefined,
  });

  if (!result.success) {
    console.warn('[DocuVault] Document deletion partially failed:', result);
  }
  return result;
}

// ── Update document metadata ──────────────────────────────────────────────────
export async function updateDocument(id, updates) {
  const res = await writeFetch(`update/${TABLE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return res.json();
}

/**
 * Update only the access fields of a document (delegation use-case).
 * Thin wrapper around updateDocument for semantic clarity.
 */
export async function updateDocumentAccess(id, accessPayload) {
  return updateDocument(id, accessPayload);
}

/**
 * submitAuthorization
 * Routes through the backend Express server which:
 *  1. Validates the approver against authorized_parties
 *  2. Checks for duplicate approvals
 *  3. Verifies the S3 object still exists (HeadObject)
 *  4. Writes to doc_authorizations_1748900000
 *  5. Updates authorization_approvals on the parent docuvault_files record
 *
 * @param {string|number} docId        - NCB record ID
 * @param {Array}         _ignored     - (kept for API compat, server fetches fresh approvals)
 * @param {object}        approverInfo - { name, email, role?, notes?, typedSignature }
 * @param {string}        s3Key        - S3 object key for the document
 */
export async function submitAuthorization(docId, _ignored, approverInfo, s3Key) {
  const user = getSession();
  if (!user) throw new Error('Not authenticated');

  return submitAuthorizationToServer({
    docRecordId:    docId,
    ownerId:        user.id,
    s3Key:          s3Key || '',
    approverName:   approverInfo.name,
    approverEmail:  approverInfo.email,
    approverRole:   approverInfo.role || 'executor',
    notes:          approverInfo.notes || '',
    typedSignature: approverInfo.typedSignature || '',
  });
}

/**
 * fetchAuthorizations
 * Returns all authorization records for a document from the backend.
 */
export async function fetchAuthorizations(docId) {
  const user = getSession();
  if (!user) return [];
  return fetchAuthorizationsFromServer({ docRecordId: docId, ownerId: user.id });
}

// ── Expiry Alert Helpers ────────────────────────────────────────────────────

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Returns the expiry status for a single document.
 * @returns {'none' | 'expired' | 'critical' | 'warning' | 'info'}
 */
export function getExpiryStatus(doc) {
  if (!doc?.expiry_date) return 'none';
  const expiry = new Date(doc.expiry_date);
  const now = new Date();
  const daysUntil = Math.ceil((expiry.getTime() - now.getTime()) / MS_PER_DAY);

  if (daysUntil < 0) return 'expired';
  if (daysUntil <= 3) return 'critical';
  if (daysUntil <= 14) return 'warning';
  if (daysUntil <= 30) return 'info';
  return 'none';
}

/**
 * Returns the number of days until expiry (negative if already expired).
 */
export function getDaysUntilExpiry(doc) {
  if (!doc?.expiry_date) return null;
  const expiry = new Date(doc.expiry_date);
  const now = new Date();
  return Math.ceil((expiry.getTime() - now.getTime()) / MS_PER_DAY);
}

/**
 * Counts documents by expiry severity.
 * @returns {{ expired: number, critical: number, warning: number, info: number }}
 */
export function countExpiryAlerts(docs) {
  return docs.reduce((acc, doc) => {
    const status = getExpiryStatus(doc);
    if (status === 'expired') acc.expired += 1;
    else if (status === 'critical') acc.critical += 1;
    else if (status === 'warning') acc.warning += 1;
    else if (status === 'info') acc.info += 1;
    return acc;
  }, { expired: 0, critical: 0, warning: 0, info: 0 });
}

/**
 * Sorts documents so that expired / soon-to-expire items appear first.
 */
export function sortByExpiryUrgency(docs) {
  const priority = { expired: 0, critical: 1, warning: 2, info: 3, none: 4 };
  return [...docs].sort((a, b) => {
    const pa = priority[getExpiryStatus(a)];
    const pb = priority[getExpiryStatus(b)];
    if (pa !== pb) return pa - pb;
    // Secondary: closer expiry first
    const da = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
    const db = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
    return da - db;
  });
}

// ── Expiry Reminder Preferences (localStorage) ──────────────────────────────

const PREFS_KEY        = 'docuvault_reminder_days';
const DISMISSED_KEY    = 'docuvault_dismissed_reminders';
const DEFAULT_REMINDER_DAYS = 30;

/**
 * Returns the user's reminder-days preference (how many days before expiry
 * a document should appear as a reminder). Defaults to 30.
 */
export function getReminderDays() {
  try {
    const v = parseInt(localStorage.getItem(PREFS_KEY), 10);
    return isNaN(v) || v < 1 ? DEFAULT_REMINDER_DAYS : v;
  } catch {
    return DEFAULT_REMINDER_DAYS;
  }
}

/**
 * Sets the reminder-days preference.
 */
export function setReminderDays(days) {
  try {
    const v = parseInt(days, 10);
    if (!isNaN(v) && v >= 1) localStorage.setItem(PREFS_KEY, String(v));
  } catch { /* private mode */ }
}

/**
 * Returns the dismissed/snoozed reminder records.
 * Shape: [{ docId, dismissedAt, snoozeUntil }]
 * - snoozeUntil = null  → permanently dismissed
 * - snoozeUntil = ISO   → snoozed until that timestamp
 */
export function getDismissedReminders() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Permanently dismisses a reminder for a document.
 */
export function dismissReminder(docId) {
  try {
    const list = getDismissedReminders().filter(r => r.docId !== docId);
    list.push({ docId, dismissedAt: new Date().toISOString(), snoozeUntil: null });
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
  } catch { /* private mode */ }
}

/**
 * Snoozes a reminder for a document for the given number of days.
 */
export function snoozeReminder(docId, days) {
  try {
    const list = getDismissedReminders().filter(r => r.docId !== docId);
    const snoozeUntil = new Date(Date.now() + days * MS_PER_DAY).toISOString();
    list.push({ docId, dismissedAt: new Date().toISOString(), snoozeUntil });
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
  } catch { /* private mode */ }
}

/**
 * Clears a dismissed/snoozed reminder for a document (e.g. after renewing).
 */
export function clearDismissedReminder(docId) {
  try {
    const list = getDismissedReminders().filter(r => r.docId !== docId);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
  } catch { /* private mode */ }
}

/**
 * Returns documents that have active (non-dismissed, non-snoozed) expiry
 * reminders within the user's configured reminder-days window.
 * Expired documents are always included regardless of the days preference.
 */
export function getActiveExpiryReminders(docs) {
  const reminderDays = getReminderDays();
  const dismissed    = getDismissedReminders();
  const now          = Date.now();

  const isSnoozed = (docId) => {
    const rec = dismissed.find(r => String(r.docId) === String(docId));
    if (!rec) return false;
    if (rec.snoozeUntil === null) return true; // permanently dismissed
    return new Date(rec.snoozeUntil).getTime() > now; // still snoozed
  };

  return docs.filter(doc => {
    if (!doc?.expiry_date) return false;
    if (isSnoozed(doc.id)) return false;
    const days = getDaysUntilExpiry(doc);
    if (days === null) return false;
    // Expired documents always show; upcoming ones respect the preference
    return days < 0 || days <= reminderDays;
  });
}

/**
 * Extends a document's expiry date by calling updateDocument with a new date.
 * @param {string|number} docId
 * @param {string} newExpiryDate  - ISO date string (YYYY-MM-DD)
 */
export async function renewExpiryDate(docId, newExpiryDate) {
  return updateDocument(docId, { expiry_date: newExpiryDate });
}

// ── Access Log (localStorage) ──────────────────────────────────────────────────

const ACCESS_LOG_KEY = 'docuvault_access_logs';
const MAX_LOG_ENTRIES = 500;

export const ACCESS_EVENT_TYPES = {
  view:      { label: 'Viewed',     color: 'text-blue-500',    bg: 'bg-blue-50'   },
  download:  { label: 'Downloaded', color: 'text-green-500',   bg: 'bg-green-50'  },
  upload:    { label: 'Uploaded',    color: 'text-amber-500',   bg: 'bg-amber-50'  },
  authorize: { label: 'Authorized',  color: 'text-purple-500',  bg: 'bg-purple-50' },
  delete:    { label: 'Deleted',     color: 'text-red-500',     bg: 'bg-red-50'    },
  delegate:  { label: 'Delegated',   color: 'text-indigo-500',  bg: 'bg-indigo-50' },
  renew:     { label: 'Renewed',     color: 'text-teal-500',    bg: 'bg-teal-50'   },
};

/**
 * Logs an access event for a document.
 * @param {string|number} docId
 * @param {string} eventType - one of ACCESS_EVENT_TYPES keys
 * @param {object} details - additional context (docName, s3Key, etc.)
 */
export function logAccess(docId, eventType, details = {}) {
  try {
    const user = getSession();
    const entry = {
      id: crypto.randomUUID(),
      docId: String(docId),
      docName: details.docName || '',
      eventType,
      timestamp: new Date().toISOString(),
      userId: user?.id || 'unknown',
      userName: user?.name || user?.email || 'Unknown',
      details: {
        ...details,
        userAgent: navigator.userAgent.slice(0, 200),
      },
    };
    const logs = getAccessLogs();
    logs.unshift(entry);
    if (logs.length > MAX_LOG_ENTRIES) logs.length = MAX_LOG_ENTRIES;
    localStorage.setItem(ACCESS_LOG_KEY, JSON.stringify(logs));
    return entry;
  } catch { /* private mode */ }
  return null;
}

/**
 * Returns access log entries, optionally filtered by document ID.
 * @param {string|number|null} docId - pass null/undefined for all entries
 */
export function getAccessLogs(docId = null) {
  try {
    const raw = localStorage.getItem(ACCESS_LOG_KEY);
    if (!raw) return [];
    const logs = JSON.parse(raw);
    if (!Array.isArray(logs)) return [];
    if (docId) return logs.filter(l => String(l.docId) === String(docId));
    return logs;
  } catch {
    return [];
  }
}

/**
 * Clears access log entries, optionally only for a specific document.
 * @param {string|number|null} docId - pass null/undefined to clear all
 */
export function clearAccessLogs(docId = null) {
  try {
    if (docId) {
      const logs = getAccessLogs().filter(l => String(l.docId) !== String(docId));
      localStorage.setItem(ACCESS_LOG_KEY, JSON.stringify(logs));
    } else {
      localStorage.removeItem(ACCESS_LOG_KEY);
    }
  } catch { /* private mode */ }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

export const DOCUMENT_CATEGORIES = [
  { id: 'all',       label: 'All Documents',    icon: 'FiFolder',      color: 'text-gray-500'   },
  { id: 'will',      label: 'Wills & Trusts',   icon: 'FiFileText',    color: 'text-blue-500'   },
  { id: 'property',  label: 'Property & Deeds', icon: 'FiHome',        color: 'text-green-500'  },
  { id: 'financial', label: 'Financial',         icon: 'FiDollarSign',  color: 'text-yellow-500' },
  { id: 'medical',   label: 'Medical & POA',    icon: 'FiHeart',       color: 'text-red-500'    },
  { id: 'insurance', label: 'Insurance',         icon: 'FiShield',      color: 'text-purple-500' },
  { id: 'identity',  label: 'Identity Docs',    icon: 'FiUser',        color: 'text-indigo-500' },
  { id: 'funeral',   label: 'Funeral Plans',    icon: 'FiFeather',     color: 'text-pink-500'   },
  { id: 'general',   label: 'General',           icon: 'FiArchive',     color: 'text-gray-400'   },
];

export const ACCESS_LEVELS = [
  { id: 'private',     label: 'Private — Owner Only',      desc: 'Only you can access this document.'                         },
  { id: 'executor',    label: 'Executor Access',            desc: 'Accessible by your designated executor upon authorization.' },
  { id: 'beneficiary', label: 'Beneficiary Access',         desc: 'Shared with listed beneficiaries upon authorization.'      },
  { id: 'legal',       label: 'Legal Counsel Access',       desc: 'Accessible by your attorney upon request.'                 },
  { id: 'multi_party', label: 'Multi-Party Authorization',  desc: 'Requires approval from multiple authorized parties.'       },
];